import { PageDesc } from '@fullstory/client-api';
import {
  NamedElementRecord,
  ElementPropertyRule,
} from '@fullstory/proto/fs/services/search-settings';
import { RawEvent } from '@fullstory/recording-api';
import { Point } from '@fullstory/util/geometry';
import { RendererDimensions, RendererEvent } from './events';
import { ObjectCollection, ObjectInfo, ObjectQuery } from './queries';

export const ROBOTO_CSS = 'https://fonts.googleapis.com/css?family=Roboto';

/**
 * @deprecated use the enum defined in @fullstory/playback-model
 */
export enum ActivityState {
  Closed = 0, // Page is definitely closed.
  Sleeping = 1, // Machine is sleeping, page not known to be closed (but may be).
  Unfocused = 2, // Page is not focused.
  Inactive = 3, // Inactive, but still focused.
  Active = 4, // Actively interacting with the page.
}

// Teeny tiny utility for interpreting ActivityState.
const AS = ActivityState;
/**
 * @deprecated use the utils in @fullstory/playback-model instead
 */
export const activityInfo = {
  active: (a: ActivityState): boolean => a === AS.Active,
  focused: (a: ActivityState): boolean => a === AS.Active || a === AS.Inactive,
} as const;

/** Options for Renderer#playPage(). */
export type PlayPageOptions = {
  /**
   * The timestamp that we should seek to before displaying the page for the
   * first time. If unset, defaults to the timestamp of the first visible
   * content on the page.
   */
  initialTimeStampMs?: number;
};

interface RendererOperationResult {
  type: 'web' | 'mobile' | 'server';
}

/** Playback information from playPage specific to mobile */
interface MobilePlayResult extends RendererOperationResult {
  type: 'mobile';
  pageOffsetMs: number;
  /** The total number of frames in this session. */
  totalFrames: number;
  totalTimeMs: number;
}

interface WebPlayResult extends RendererOperationResult {
  type: 'web';
  pageOffsetMs: number;
  totalTimeMs: number;
}

/** The return type of Renderer#playPage(). */
export type PlayResult = MobilePlayResult | WebPlayResult | void;

/** Playback information from playToTime specific to mobile */
interface MobileSeekResult extends RendererOperationResult {
  type: 'mobile';
  /** The frame numbers that will be interpolated between to render the requested timestamp. */
  frameA: number;
  frameB: number;
  /** The URL of the frame bundle that contains `frame`. */
  bundle: string;
  /* timestamps for frame-by-frame playback */
  nextFrameTs: number | null;
  prevFrameTs: number | null;
  exactFrame: number | null; // usually null, unless we are at the exact beginning/end of an interpolation
  pageOffsetMs: number;
  /* Total time it took to complete the seek operation */
  totalTimeMs: number;
}

interface WebSeekResult extends RendererOperationResult {
  type: 'web';
  pageOffsetMs: number;
  totalTimeMs: number;
}

/** The return type of Renderer#playToTime(). */
export type SeekResult = MobileSeekResult | WebSeekResult | void;

export type HighlightElementsBySelectors = {
  type: 'css-selectors';
  targetSelectors: string[];
};
export type HighlightElementByIds = {
  type: 'fs-ids';
  fId: number;
  fsId?: number;
  pIds?: number[];
};
export type HighlightElementIdentifiers = HighlightElementsBySelectors | HighlightElementByIds;

export interface ViewportOverrides {
  /**
   * Ignore session height. Take up the entire height of playback, even after resizing the playback/browser window.
   * Setting this to true also keeps the playback to the top of the page.
   */
  useFullPlaybackHeight?: boolean;
  // TODO Add width override here as part of sc-198945 to support consistent Heatmaps
}

/**
 * Cursor types that the renderer can display. Use 'captured' to display the
 * cursor that would've been shown on the captured page. The other values are
 * CSS 'cursor:' property values.
 */
export type RendererCursor = 'captured' | 'crosshair' | 'default' | 'pointer';

/** Options for the various modes that the renderer supports. */
export type RendererModeOptions =
  | {
      mode: 'playback';
    }
  | {
      mode: 'interactive';
      cursor?: RendererCursor;
    };
export type RendererMode = RendererModeOptions['mode'];

export interface GroupByPropertyDisplay {
  /**
   * The name of the property being used for grouping.
   * Omits the elem_ scope prefix (as it is implied).
   */
  propertyName: string;

  /**
   * The applicable extracted property rules that should be applied to produce property values from the preview.
   */
  propertyRules: ResolvedElementPropertyRule[];
}

export type ResolvedElementPropertyRule = Omit<
  ElementPropertyRule,
  'namedElementId' | 'childNamedElementId'
>;

export interface FilteredElementRecord {
  /**
   * The named element record that was filtered.
   * If group by property is enabled, the same named element record can be given multiple times with different property values.
   */
  namedElementRecord: NamedElementRecord;
  /**
   * If defined, the property value for the filtered named element record.
   */
  propertyValue: string | undefined;
  /**
   * Whether or not the filtered element actually satisfies the constraints (like focus area).
   * If false, the element merely exists as an element on the page outside of the constraints.
   */
  satisfiesConstraints: boolean;
}

// Interface for the remote off-domain page Renderer. There are separate backend implementations for this
// for Web and Android. But they both share this client interface on the side of the main Application.
export interface Renderer {
  openPage(page: PageDesc): void;

  // TODO(sethf): Make the 'options' parameter non-optional after June 2020.
  // It's only optional to support old versions of 'fsui.js' being used with
  // newer versions of 'fsrenderer.js'.
  playPage(raw: any, options?: PlayPageOptions): Promise<PlayResult>;

  // Plays a Renderer to the specified pageOffset time. If a requestId is
  // provided, the Renderer will make a call to onPlayToTimeCommitted() when all
  // events up to `pageOffset` have been played back.
  playToTime(pageOffset: number, requestId?: number | string): Promise<SeekResult>;

  // Notify that playback is paused. This gives the Renderer the opportunity to
  // conserve resources by disabling background processes.
  onPause(): Promise<void>;

  // Append trailing events to an existing Renderer.
  appendTrailingEvents(trailingEvents: RawEvent[]): void;

  /**
   * higlights an element on the frame.
   * @deprecated, use {@link queryObjects} or {@link queryObjectsAtPoint} instead.
   */
  highlightElements(highlightElementIdentifiers?: HighlightElementIdentifiers): void;

  /**
   * Enable page insights mode.
   *
   * @deprecated TODO(sethf): Move this to setMode().
   */
  enterPageInsights(): void;

  /**
   * Disable page insights mode.
   *
   * @deprecated TODO(sethf): Move this to setMode().
   */
  leavePageInsights(): void;

  /**
   * Perform actions needed so that the playback is not scaled.
   *
   * @deprecated TODO(sethf): Move this to setMode().
   */
  enableFullPageMode(): void;

  /**
   * Perform actions needed so that the playback is more likely to appear above
   * screen fold.
   *
   * @deprecated TODO(sethf): Move this to setMode().
   */
  enableHeatmapsExploreMode(): void;

  /** Determines if the current page is internal scrolling with the given internal scroll selectors. */
  isInternalScrollingPage(selectors: string[]): boolean;

  /**
   * Continuously observe the rendered objects which match the given query.
   *
   * When observeObjects() is called, or when a possible change in any observed
   * object is detected thereafter, a RendererObservationEvent will be sent to
   * the host with up-to-date information about all observed objects. The stream
   * of observations can be stopped by calling observeObjects() with an empty
   * query.
   */
  observeObjects(query: ObjectQuery): void;

  /**
   * @returns information about the rendered objects which match the given
   * query.
   */
  queryObjects(query: ObjectQuery): ObjectCollection;

  /**
   * @returns information about the rendered objects which intersect the given
   * point. The results are ordered by depth; the first result is the topmost
   * object in the stack. The point is interpreted relative to the renderer
   * element; (0, 0) corresponds to the top left corner of the renderer.
   */
  queryObjectsAtPoint(point: Point): ObjectInfo[];

  /** @deprecated Use queryObjectsAtPoint() instead. */
  queryPoint(point: Point): ObjectInfo[];

  /**
   * @returns information about the rendered objects which match the given
   * selector.
   *
   * @deprecated Use queryObjects() instead.
   */
  querySelector(selector: string): ObjectInfo[];

  /**
   * @returns whether or not testPath is a descendant of basePath
   * @param testPath the path being tested as a descendant
   * @param basePath the base path being tested as an ancestor
   */
  isDescendant(testPath: string, basePath: string): boolean;

  scrollToElement(
    elementSelector: string,
    propertyValue: string | undefined,
    focusSelectors: string[],
    groupByProperty: GroupByPropertyDisplay | undefined,
  ): void;

  /**
   * Given a mapping of property names to display configurations, returns which properties are available among the rendered elements.
   */
  computeAvailableProperties(
    focusSelectors: string[],
    allGroupByProperty: Map<string, GroupByPropertyDisplay>,
  ): string[];

  /**
   * Given a groupByProperty setting, computes all possible property values from rendered elements.
   * The resulting properties are normalized based on property type.
   */
  computePropertyValues(
    focusSelectors: string[],
    groupByProperty: GroupByPropertyDisplay | undefined,
  ): string[];

  /** @deprecated TODO(sethf): Move this to setMode(). */
  setViewportOverrides(viewportOverrides: ViewportOverrides): void;

  /**
   * Resets all current scrollable items to the top left.
   */
  zeroCurrentScrollPosition(): void;

  /**
   * Finds which named elements exist and are visible on the current page.
   * @deprecated use filterRelatedElements instead.
   */
  filterRelatedNamedElements(namedElements: NamedElementRecord[]): NamedElementRecord[];

  /**
   * Finds which named elements exist and are visible on the current page.
   * Additionally, finds the corresponding property values for the elements on the current page, when group by is configured.
   */
  filterRelatedElements(
    focusSelectors: string[],
    namedElements: NamedElementRecord[],
    groupByProperty: GroupByPropertyDisplay | undefined,
  ): FilteredElementRecord[];

  /** Waits until all <img/> elements are loaded and also turn lazy loading images to eager. */
  waitForImagesToLoad(maxTimeoutMs: number): Promise<void>;

  /**
   * Determines whether or not there is a webview at the current offsetMs. Optionally searches for
   * only webviews that are captured.
   * @param offsetMs Relative offset in ms from the start of the page
   * @param opts Options for the search
   * @param opts.onlyIncludeRecorded If true, only returns true if webview at the given offsetMs is recorded.
   * @returns true if the page has a webview at the given offsetMs
   */
  hasWebViewAt(offsetMs: number, opts: { onlyIncludeRecorded?: boolean }): Promise<boolean>;

  // When in page insights mode, these are valid switches.
  /** @deprecated TODO(sethf): Move this to setMode(). */
  showInspector(): void;
  /** @deprecated TODO(sethf): Move this to setMode(). */
  showActivityMaps(): void;
  /** @deprecated TODO(sethf): Move this to setMode(). */
  showScrollMaps(): void;

  /**
   * Set the renderer's mode.
   *
   * TODO(sethf): Shift all existing modes over to this interface.
   */
  setMode(options: RendererModeOptions): Promise<void>;

  /** @returns the current dimensions of the renderer. */
  getDimensions(): Promise<RendererDimensions>;

  /**
   * Gets the full HTML content of the playback frame as a string.
   *
   * @returns The full HTML content of the playback frame as a string, or null if not available
   */
  getPlaybackFrameHTML(): string | null;

  /**
   * Sets the text corpus for the renderer. This is used to replace text in the playback frame
   * with the given text when matched.
   *
   * @param textCorpus A mapping of text to replace with.
   */
  setTextCorpus(textCorpus: Record<string, string>): void;

  teardown(): void;
}

export type ResourceEvent =
  | {
      kind: 'initialLoadComplete';
    }
  | {
      kind: 'resourceLoadStart';
      href: string;
      destination: RequestDestination;
    }
  | {
      kind: 'resourceLoadFailed';
      href: string;
      destination: RequestDestination;
    }
  | {
      kind: 'resourceMapInvalid';
    };

// This is the thing that the Renderer will call back out into in order to change things in the main page.
export interface RendererHost {
  /** Notifies the host about an event that occurred in the renderer. */
  onEvent(event: RendererEvent): void;

  /**
   * A log message from the renderer. These are bounced to the host when we
   * detect we're running in a test environment so that reftests and similar
   * test frameworks can capture renderer-level log messages.
   */
  log(level: keyof Console, message: string): void;

  /**
   * An FS custom event from the renderer. This should only be used for
   * data that is _safe_ to capture in FS events!
   */
  recordCustomEvent(event: string, properties: { [name: string]: any }): void;

  /**
   * Notifies the host about resource-related events so that the host can
   * provide feedback in the UI.
   */
  onResourceEvent(event: ResourceEvent): void;

  // Notifies that the playToTime() call with the corresponding requestId has
  // been played back completely. Note that this is not called unless a
  // requestId was passed to playToTime().
  onPlayToTimeCommitted(requestId: number | string): void;

  setPageActivity(state: ActivityState): void;

  // TODO(sethf): This is no longer used by 'fsui.js', but we retain it for now
  // to support using newer versions of 'fsrenderer.js' with older versions of
  // 'fsui.js'. Remove this method after June 2020.
  // TODO(jordanhurt): remove after the AppPlaybackFirstInsertWhenRefactor flag
  setFirstInsertWhen(pageOffset: number): void;
}

// Client stubs for auto-proxying.
// TODO(jaime): Code gen this.
export class RendererClientStub implements Renderer {
  openPage(_page: PageDesc) {}
  async playPage(_raw: any, _options?: PlayPageOptions): Promise<PlayResult> {}
  async playToTime(_pageOffset: number, _requestId?: number | string): Promise<SeekResult> {}
  async onPause(): Promise<void> {}
  appendTrailingEvents(_trailingEvents: RawEvent[]) {}
  async highlightElements(_highlightElementIdentifiers?: HighlightElementIdentifiers) {}
  enterPageInsights() {}
  enableFullPageMode() {}
  leavePageInsights() {}
  showInspector() {}
  showActivityMaps() {}
  showScrollMaps() {}
  hideGlassPane() {}
  showGlassPane() {}
  teardown() {}
  enableHeatmapsExploreMode() {}
  isInternalScrollingPage(_selectors: string[]): boolean {
    return false;
  }
  scrollToElement(
    _elementSelector: string,
    _propertyValue: string | undefined,
    _focusSelectors: string[],
    _groupByProperty: GroupByPropertyDisplay | undefined,
  ) {}
  computeAvailableProperties(
    focusSelectors: string[],
    allGroupByProperty: Map<string, GroupByPropertyDisplay>,
  ): string[] {
    return [];
  }
  computePropertyValues(
    focusSelectors: string[],
    groupByProperty: GroupByPropertyDisplay | undefined,
  ): string[] {
    return [];
  }
  zeroCurrentScrollPosition() {}
  setViewportOverrides(_viewportOverrides: ViewportOverrides) {}
  filterRelatedNamedElements(_namedElements: NamedElementRecord[]): NamedElementRecord[] {
    return [];
  }
  filterRelatedElements(
    _focusSelectors: string[],
    _namedElements: NamedElementRecord[],
    _groupByProperty: GroupByPropertyDisplay | undefined,
  ): FilteredElementRecord[] {
    return [];
  }
  observeObjects(_query: ObjectQuery) {}
  queryObjects(_query: ObjectQuery): ObjectCollection {
    return {};
  }
  queryObjectsAtPoint(_point: Point): ObjectInfo[] {
    return [];
  }
  queryPoint(_point: Point): ObjectInfo[] {
    return [];
  }
  querySelector(_selector: string): ObjectInfo[] {
    return [];
  }
  isDescendant(testPath: string, basePath: string): boolean {
    return false;
  }
  async waitForImagesToLoad(_maxTimeoutMs: number): Promise<void> {}
  hasWebViewAt(_offsetMs: number, _opts: { onlyIncludeRecorded?: boolean } = {}): Promise<boolean> {
    return Promise.resolve(false);
  }
  async setMode(_options: RendererModeOptions): Promise<void> {}
  async getDimensions(): Promise<RendererDimensions> {
    return { contentArea: new DOMRect(), rendererArea: new DOMRect() };
  }
  getPlaybackFrameHTML(): string | null {
    return null;
  }
  setTextCorpus(_textCorpus: Record<string, string>): void {}
}

export class RendererHostClientStub implements RendererHost {
  onEvent(_event: RendererEvent): void {}
  log(_level: keyof Console, _message: string): void {}
  recordCustomEvent(_event: string, _properties: { [name: string]: any }): void {}
  onResourceEvent(_event: ResourceEvent): void {}
  notifyResourceErrors(_errors: number, _total: number, _forceMessage: boolean) {}
  onPlayToTimeCommitted(_requestId: number | string) {}
  setPageActivity(_state: ActivityState) {}
  // TODO(jordanhurt): remove after the AppPlaybackFirstInsertWhenRefactor flag
  setFirstInsertWhen(_pageOffset: number) {}
}
